import React, { useEffect } from "react";
import { useApplication } from "../ApplicationProvider";
import { usePublicConfig } from "../PublicConfigProvider";
import { RequiredFormsHub } from "../RequiredFormsHub";
import { GUIDE_HREF } from "../Header";
import { computeAgeAsOf, formatIsoDate, formatSessionDateRange } from "../../config";
import { studentParentEmailsMatch, phonePlaceholderFor } from "../../validation";
import { PhoneInput } from "../PhoneInput";
import {
  ADDRESS_TYPES,
  ADDRESS_TYPE_LABELS,
  APO_EXAMPLES,
  APO_OFFICES,
  APO_OFFICE_LABELS,
  APO_STATE_CODES,
  APO_STATE_CODE_LABELS,
  FOREIGN_LINE_KEYS,
  OCONUS,
  USPS_INTERNATIONAL_URL,
  US_STATES,
  isUsStateCode,
  normalizeAddressType,
  type AddressType,
} from "../../mailingAddress";

export function Step1Program({ errors }: { errors: Record<string, string> }) {
  const { data, update, saveState, botField, setBotField } = useApplication();
  const cfg = usePublicConfig();
  const dateRange = formatSessionDateRange(cfg.sessionStartDate, cfg.sessionEndDate);

  // Program name shown in the attestation. cfg.programName renders "Maryland Boys
  // State" / "Maryland Girls State" from PROGRAM_NAME. To use the shorter "MD Boys
  // State" / "MD Girls State" wording instead, swap `cfg.programName` below for
  // `cfg.programShort`-based text (or change PROGRAM_NAME in each app's .env).
  const programName = cfg.programName;

  // A 409 from the draft-create path means another application already exists for
  // this student email. The top-of-page banner can scroll out of view while the
  // applicant works through Step 1, so we mirror the same expanded message inline
  // directly under the student-email field.
  const duplicateEmail = saveState === "conflict";

  // Live, client-side mirror of the schema rule: as soon as the student and
  // parent/guardian emails are both present and equal (normalized), show an amber
  // message under the parent field. This standalone check runs independently of
  // the rest of Step 1, so it surfaces on a mostly-empty step where the
  // object-level superRefine can't (Zod skips it while the object parse aborts on
  // missing required fields).
  const emailsMatch = studentParentEmailsMatch(data.step2.studentEmail, data.step2.parentEmail);

  // Round 22 (R-02) — which mailing-address field set to render. Read through
  // normalizeAddressType so a draft saved before this round (no addressType key)
  // renders the US set exactly as it always did.
  const addressType = normalizeAddressType(data.step2.addressType);
  // Round 24 — the format hint follows the address type, so an overseas family
  // is not shown a US pattern they do not have to follow.
  const phonePlaceholder = phonePlaceholderFor(addressType);

  const set = <K extends keyof typeof data.step2>(key: K, value: (typeof data.step2)[K]) =>
    update((d) => {
      d.step2[key] = value as never;
    });

  // Age is derived from DOB as of the session start date, not entered. Keep the
  // stored value (which flows to the tracker's Age column) in sync with the
  // derived value, including drafts saved before age became read-only.
  const derivedAge = computeAgeAsOf(data.step2.dob, cfg.sessionStartDate);
  useEffect(() => {
    if (data.step2.ageAtProgram !== derivedAge) {
      set("ageAtProgram", derivedAge);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedAge]);

  return (
    <>
    {/* Round 47 — one quiet line, not a callout: Step 1 is already the busiest
        screen. An ordinary link (new tab, so nothing typed is lost), outside the
        form fields, so the Step 1 gate never sees it. */}
    {cfg.guideLinkEnabled && (
      <p className="text-sm text-slate-600 mb-3">
        Need help?{" "}
        <a className="underline text-program" href={GUIDE_HREF} target="_blank" rel="noopener">
          View the Application Guide (PDF)
        </a>
        .
      </p>
    )}
    <section className="section-card">
      <h2 className="text-xl font-semibold mb-1">Program Confirmation</h2>
      <p className="text-sm text-slate-600 mb-4">Confirm the program and session details below.</p>

      <div className="rounded-md border border-program/30 bg-program/5 p-4 mb-6 space-y-2">
        <p className="text-base">
          <strong>Program:</strong> {cfg.programName}
        </p>
        <p className="text-base">
          <strong>Session:</strong> {dateRange}
        </p>
        <p className="text-sm text-slate-700">
          For final details including session location, visit{" "}
          <a className="underline text-program" href={cfg.programInfoUrl} target="_blank" rel="noreferrer">
            {cfg.programInfoUrl}
          </a>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="field-label" htmlFor="sessionYear">Session year</label>
          <input
            id="sessionYear"
            type="number"
            className="field-input"
            value={data.step1.sessionYear}
            onChange={(e) =>
              update((d) => {
                d.step1.sessionYear = Number(e.target.value) || cfg.sessionYear;
              })
            }
          />
          {errors["sessionYear"] && <p className="field-error">{errors["sessionYear"]}</p>}
        </div>
      </div>

      <div className="mt-6 rounded-md border border-program/30 bg-program/5 p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-slate-300 text-program focus:ring-program"
            checked={data.step1.attestationAcknowledged}
            onChange={(e) =>
              update((d) => {
                d.step1.attestationAcknowledged = e.target.checked;
              })
            }
          />
          <span className="text-sm text-slate-800">
            I understand that attending the entire <strong>{programName}</strong> session is required
            and I will be expected to secure a sponsor Post or Unit for my attendance. Completion of
            the application is the first step and should be done by early March to ensure time to
            secure a sponsor.
          </span>
        </label>
        {errors["attestationAcknowledged"] && (
          <p className="field-error mt-2">{errors["attestationAcknowledged"]}</p>
        )}
      </div>
    </section>

    {/* Honeypot (bot protection): off-screen, never tab-focusable, with a name
        browser autofill cannot categorize. The previous name "company_website"
        matched Chrome's company/website autofill fields, so accepting an email
        autofill suggestion ALSO populated this hidden input — which tripped the
        server bot check and SILENTLY dropped a real applicant's draft. The
        neutral, non-PII name is the real defense here: autocomplete="off" is
        ignored by Chrome for saved profiles, so we do not rely on it. A real
        applicant never sees or fills it; a bot that fills every field still
        trips it. Do NOT rename this to any profile-like word (name / email /
        phone / address / city / state / zip / company / organization / website /
        url …), and do NOT add aria-hidden (bots skip aria-hidden fields). */}
    <div
      style={{
        position: "absolute",
        left: "-10000px",
        top: "auto",
        width: "1px",
        height: "1px",
        overflow: "hidden",
      }}
    >
      <label htmlFor="mdlegion_hp">Leave this field empty</label>
      <input
        id="mdlegion_hp"
        name="mdlegion_hp"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={botField}
        onChange={(e) => setBotField(e.target.value)}
      />
    </div>

    <section className="section-card">
      <h2 className="text-xl font-semibold mb-1">Student &amp; Parent Email</h2>
      <p className="text-sm text-slate-600 mb-4">
        We use these addresses to email you a link so you can save your progress and return later, and to send
        your submission confirmation.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="field-label" htmlFor="studentEmail">Student email</label>
          <input
            id="studentEmail"
            type="email"
            className="field-input"
            value={data.step2.studentEmail}
            onChange={(e) => set("studentEmail", e.target.value)}
          />
          <p className="mt-1 text-xs text-slate-500">
            Use the student's own email address — not a parent's. Each applicant needs a unique
            email, so siblings or twins can't share one.
          </p>
          {errors["studentEmail"] && <p className="field-error">{errors["studentEmail"]}</p>}
          {duplicateEmail && (
            <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3">
              <p className="text-sm text-amber-900">
                A saved application already exists for{" "}
                <strong>{data.step2.studentEmail}</strong>. Each applicant must use their own unique
                email address — siblings and twins in the same family each need a separate email,
                and this should be the student's own email, not a parent's. If you're a parent
                completing this for your child, enter the student's email address here. We've
                emailed the existing application's resume link to the addresses on file so it can be
                continued. (Edits on this page are kept on this device only until then.)
              </p>
            </div>
          )}
        </div>
        <div>
          <label className="field-label" htmlFor="parentEmail">Parent / Guardian email</label>
          <input
            id="parentEmail"
            type="email"
            className="field-input"
            value={data.step2.parentEmail}
            onChange={(e) => set("parentEmail", e.target.value)}
          />
          {errors["parentEmail"] && <p className="field-error">{errors["parentEmail"]}</p>}
          {emailsMatch && (
            <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3">
              <p className="text-sm text-amber-900">
                This matches the student's email. The student and the parent/guardian each need
                their own email address, so both receive their own save-and-resume links and
                confirmations. Please enter a different address here.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>

    <section className="section-card">
      <h2 className="text-xl font-semibold mb-1">Student Basic Information</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="field-label">Legal first name</label>
          <input className="field-input" value={data.step2.legalFirstName} onChange={(e) => set("legalFirstName", e.target.value)} />
          {errors["legalFirstName"] && <p className="field-error">{errors["legalFirstName"]}</p>}
        </div>
        <div>
          <label className="field-label">Legal middle name</label>
          <input className="field-input" value={data.step2.legalMiddleName} onChange={(e) => set("legalMiddleName", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Legal last name</label>
          <input className="field-input" value={data.step2.legalLastName} onChange={(e) => set("legalLastName", e.target.value)} />
          {errors["legalLastName"] && <p className="field-error">{errors["legalLastName"]}</p>}
        </div>
        <div>
          <label className="field-label">Suffix (optional)</label>
          <input
            className="field-input"
            placeholder="e.g. Jr., Sr., II, III, IV"
            maxLength={10}
            value={data.step2.suffix}
            onChange={(e) => set("suffix", e.target.value)}
          />
          {errors["suffix"] && <p className="field-error">{errors["suffix"]}</p>}
        </div>
        <div>
          <label className="field-label">Preferred name</label>
          <input className="field-input" value={data.step2.preferredName} onChange={(e) => set("preferredName", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Date of birth</label>
          <input type="date" className="field-input" value={data.step2.dob} onChange={(e) => set("dob", e.target.value)} />
          {errors["dob"] && <p className="field-error">{errors["dob"]}</p>}
        </div>
        <div>
          <label className="field-label">Age at program time</label>
          <input
            type="text"
            className="field-input bg-slate-100 text-slate-700"
            value={derivedAge ?? "—"}
            readOnly
            disabled
          />
          <p className="field-hint mt-1">
            Calculated from date of birth as of the {cfg.sessionYear} session start
            ({formatIsoDate(cfg.sessionStartDate)}).
          </p>
          {errors["ageAtProgram"] && <p className="field-error">{errors["ageAtProgram"]}</p>}
        </div>
        <div>
          <label className="field-label">Gender</label>
          <select className="field-input" value={data.step2.gender} onChange={(e) => set("gender", e.target.value as typeof data.step2.gender)}>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>
        <div>
          <label className="field-label">T-shirt size</label>
          <select className="field-input" value={data.step2.tshirtSize} onChange={(e) => set("tshirtSize", e.target.value as typeof data.step2.tshirtSize)}>
            <option value="">Select…</option>
            <option>XS</option>
            <option>S</option>
            <option>M</option>
            <option>L</option>
            <option>XL</option>
            <option>XXL</option>
            <option>XXXL</option>
          </select>
          {errors["tshirtSize"] && <p className="field-error">{errors["tshirtSize"]}</p>}
        </div>
      </div>
    </section>

    <section className="section-card">
      <h2 className="text-xl font-semibold mb-1">Mailing Address &amp; Contact</h2>
      <p className="text-sm text-slate-600 mb-4">
        Choose the kind of mailing address you use, then fill in the fields for that type. We use
        this address to mail program materials.
      </p>

      <div className="mb-5 max-w-md">
        <label className="field-label" htmlFor="addressType">Address type</label>
        <select
          id="addressType"
          className="field-input"
          value={addressType}
          onChange={(e) => set("addressType", e.target.value as AddressType)}
        >
          {ADDRESS_TYPES.map((t) => (
            <option key={t} value={t}>
              {ADDRESS_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </div>

      {/* Exactly one field set renders at a time, driven by the selection above.
          The unselected types' fields are never shown and never validated. */}
      {addressType === "US" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="field-label">Street address</label>
            <input className="field-input" value={data.step2.homeStreet} onChange={(e) => set("homeStreet", e.target.value)} />
            {errors["homeStreet"] && <p className="field-error">{errors["homeStreet"]}</p>}
          </div>
          <div>
            <label className="field-label">City</label>
            <input className="field-input" value={data.step2.homeCity} onChange={(e) => set("homeCity", e.target.value)} />
            {errors["homeCity"] && <p className="field-error">{errors["homeCity"]}</p>}
          </div>
          <div>
            <label className="field-label">County</label>
            <input className="field-input" value={data.step2.homeCounty} onChange={(e) => set("homeCounty", e.target.value)} />
            {errors["homeCounty"] && <p className="field-error">{errors["homeCounty"]}</p>}
          </div>
          <div>
            <label className="field-label">State</label>
            {/* No pre-selected state: the field used to default to "MD", which
                recorded Maryland for applicants who never touched it. A stored
                value outside the list (drafts saved while this was a free-text
                box) falls back to the placeholder so the applicant is prompted
                to choose — the stored value itself is left alone until they do. */}
            <select
              className="field-input"
              value={isUsStateCode(data.step2.homeState) ? data.step2.homeState : ""}
              onChange={(e) => set("homeState", e.target.value)}
            >
              <option value="">Select a state…</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
            {errors["homeState"] && <p className="field-error">{errors["homeState"]}</p>}
          </div>
          <div>
            <label className="field-label">ZIP</label>
            <input className="field-input" value={data.step2.homeZip} onChange={(e) => set("homeZip", e.target.value)} />
            {errors["homeZip"] && <p className="field-error">{errors["homeZip"]}</p>}
          </div>
        </div>
      )}

      {addressType === "APO_FPO_DPO" && (
        <>
          <div className="rounded-md border border-program/30 bg-program/5 p-4 mb-4 space-y-2">
            <p className="text-sm text-slate-800">
              Use the USPS military and diplomatic mail format. Address the mail to a{" "}
              <strong>named individual</strong> — "Any Service Member" is no longer permitted. Do
              not include a city or country name: doing so sends the mail into that country's
              postal network instead of the military one.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
              {APO_EXAMPLES.map((ex) => (
                <div key={ex.label}>
                  <p className="text-xs font-semibold text-slate-700">{ex.label}</p>
                  <pre className="mt-1 text-xs text-slate-700 whitespace-pre-wrap font-mono">
                    {ex.lines.join("\n")}
                  </pre>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-600 pt-1">
              Your county is recorded automatically as <strong>{OCONUS}</strong> (Outside the
              Continental United States).
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="field-label">Recipient's full name (add rank/unit if applicable)</label>
              <input
                className="field-input"
                placeholder="PFC JOHN DOE"
                value={data.step2.apoRecipientName}
                onChange={(e) => set("apoRecipientName", e.target.value)}
              />
              {errors["apoRecipientName"] && <p className="field-error">{errors["apoRecipientName"]}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">PSC / UNIT and Box number</label>
              <input
                className="field-input"
                placeholder="PSC 3 BOX 4120"
                value={data.step2.apoUnitBox}
                onChange={(e) => set("apoUnitBox", e.target.value)}
              />
              {errors["apoUnitBox"] && <p className="field-error">{errors["apoUnitBox"]}</p>}
            </div>
            <div>
              <label className="field-label">Post office</label>
              <select
                className="field-input"
                value={data.step2.apoOffice}
                onChange={(e) => set("apoOffice", e.target.value as typeof data.step2.apoOffice)}
              >
                <option value="">Select…</option>
                {APO_OFFICES.map((o) => (
                  <option key={o} value={o}>
                    {APO_OFFICE_LABELS[o]}
                  </option>
                ))}
              </select>
              {errors["apoOffice"] && <p className="field-error">{errors["apoOffice"]}</p>}
            </div>
            <div>
              <label className="field-label">State code</label>
              <select
                className="field-input"
                value={data.step2.apoStateCode}
                onChange={(e) => set("apoStateCode", e.target.value as typeof data.step2.apoStateCode)}
              >
                <option value="">Select…</option>
                {APO_STATE_CODES.map((c) => (
                  <option key={c} value={c}>
                    {APO_STATE_CODE_LABELS[c]}
                  </option>
                ))}
              </select>
              {errors["apoStateCode"] && <p className="field-error">{errors["apoStateCode"]}</p>}
            </div>
            <div>
              <label className="field-label">ZIP code</label>
              <input
                className="field-input"
                placeholder="09021 or 09498-0048"
                value={data.step2.apoZip}
                onChange={(e) => set("apoZip", e.target.value)}
              />
              {errors["apoZip"] && <p className="field-error">{errors["apoZip"]}</p>}
            </div>
          </div>
        </>
      )}

      {addressType === "Foreign" && (
        <>
          <div className="rounded-md border border-program/30 bg-program/5 p-4 mb-4 space-y-2">
            <p className="text-sm text-slate-800">
              Enter your address exactly as it should be written for your country, using as many of
              the five lines as you need. Include the country name on the last line you use.
            </p>
            <p className="text-sm text-slate-700">
              For formatting guidance, see the{" "}
              <a
                className="underline text-program"
                href={USPS_INTERNATIONAL_URL}
                target="_blank"
                rel="noreferrer"
              >
                USPS international addressing requirements
              </a>{" "}
              (optional).
            </p>
            <p className="text-xs text-slate-600">
              Your county is recorded automatically as <strong>{OCONUS}</strong> (Outside the
              Continental United States).
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 max-w-xl">
            {FOREIGN_LINE_KEYS.map((key, i) => (
              <div key={key}>
                <label className="field-label">
                  Address line {i + 1}
                  {i > 1 && <span className="font-normal text-slate-500"> (optional)</span>}
                </label>
                <input
                  className="field-input"
                  value={data.step2[key]}
                  onChange={(e) => set(key, e.target.value)}
                />
                {errors[key] && <p className="field-error">{errors[key]}</p>}
              </div>
            ))}
          </div>
        </>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="field-label">Cell phone</label>
          <PhoneInput
            placeholder={phonePlaceholder}
            addressType={addressType}
            value={data.step2.cellPhone}
            onChange={(v) => set("cellPhone", v)}
          />
          {errors["cellPhone"] && <p className="field-error">{errors["cellPhone"]}</p>}
        </div>
      </div>
    </section>

    <section className="section-card">
      <h2 className="text-xl font-semibold mb-1">Family Information</h2>
      <h3 className="text-sm font-semibold text-slate-700 mt-2">Parent / Guardian #1</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="field-label">Name</label>
          <input className="field-input" value={data.step2.parent1Name} onChange={(e) => set("parent1Name", e.target.value)} />
          {errors["parent1Name"] && <p className="field-error">{errors["parent1Name"]}</p>}
        </div>
        <div>
          <label className="field-label">Relationship</label>
          <input className="field-input" value={data.step2.parent1Relationship} onChange={(e) => set("parent1Relationship", e.target.value)} />
          {errors["parent1Relationship"] && <p className="field-error">{errors["parent1Relationship"]}</p>}
        </div>
        <div>
          <label className="field-label">Phone</label>
          <PhoneInput
            placeholder={phonePlaceholder}
            addressType={addressType}
            value={data.step2.parent1Phone}
            onChange={(v) => set("parent1Phone", v)}
          />
          {errors["parent1Phone"] && <p className="field-error">{errors["parent1Phone"]}</p>}
        </div>
        <div>
          <label className="field-label">Email</label>
          <input className="field-input" value={data.step2.parent1Email} onChange={(e) => set("parent1Email", e.target.value)} />
          {errors["parent1Email"] && <p className="field-error">{errors["parent1Email"]}</p>}
        </div>
      </div>
      <h3 className="text-sm font-semibold text-slate-700 mt-2">Parent / Guardian #2 (optional)</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="field-label">Name</label>
          <input className="field-input" value={data.step2.parent2Name} onChange={(e) => set("parent2Name", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Relationship</label>
          <input className="field-input" value={data.step2.parent2Relationship} onChange={(e) => set("parent2Relationship", e.target.value)} />
        </div>
        <div>
          <label className="field-label">Phone</label>
          <PhoneInput
            placeholder={phonePlaceholder}
            addressType={addressType}
            value={data.step2.parent2Phone}
            onChange={(v) => set("parent2Phone", v)}
          />
        </div>
        <div>
          <label className="field-label">Email</label>
          <input className="field-input" value={data.step2.parent2Email} onChange={(e) => set("parent2Email", e.target.value)} />
        </div>
      </div>
    </section>

    {/* Required Forms hub stays on Step 1, pinned at the bottom — it never blocks. */}
    <RequiredFormsHub />
    </>
  );
}
